import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

export default function AddEditScheme() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [pillars, setPillars] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  
  const [formData, setFormData] = useState({
    scheme_name: '',
    ministry_department: '',
    government_level: 'Central',
    launch_year: new Date().getFullYear(),
    status: 'active',
    target_beneficiaries: [],
    sector_industry: [],
    geographic_applicability: 'India',
    states_applicable: [],
    primary_pillar: '',
    themes: [],
    sub_themes: [],
    keywords: [],
    objective_summary: '',
    key_components: [],
    eligibility_criteria: '',
    financial_benefits_allocation: '',
    implementation_mechanism: '',
    application_process: '',
    timeline_deadlines: '',
    official_website: '',
    official_documents: [],
    applies_to_msmes: false,
    applies_to_ngos: false,
    applies_to_csr_programs: false
  });

  useEffect(() => {
    fetchPillars();
    if (isEdit) {
      fetchScheme();
    }
  }, [id]);

  const fetchPillars = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/schemes/taxonomy/pillars`);
      setPillars(data);
    } catch (error) {
      console.error('Error fetching pillars:', error);
    }
  };

  const fetchScheme = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/schemes/${id}`);
      setFormData(data);
      
      // Update themes based on selected pillar
      const pillar = pillars.find(p => p.pillar_name === data.primary_pillar);
      if (pillar) {
        setSelectedThemes(pillar.themes || []);
      }
    } catch (error) {
      console.error('Error fetching scheme:', error);
      alert('Failed to load scheme');
    } finally {
      setLoading(false);
    }
  };

  const handlePillarChange = (pillarName) => {
    setFormData({ ...formData, primary_pillar: pillarName, themes: [], sub_themes: [] });
    const pillar = pillars.find(p => p.pillar_name === pillarName);
    setSelectedThemes(pillar?.themes || []);
  };

  const handleArrayInput = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData({ ...formData, [field]: items });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/api/schemes/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Scheme updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/api/schemes/`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Scheme created successfully!');
      }
      
      navigate('/admin/schemes');
    } catch (error) {
      console.error('Error saving scheme:', error);
      alert('Failed to save scheme: ' + (error.response?.data?.detail || 'Please try again'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/schemes')}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Schemes
          </Button>

          <h1 className="text-3xl font-bold text-[#1E3A5F] mb-8">
            {isEdit ? 'Edit Scheme' : 'Add New Scheme'}
          </h1>

          <form onSubmit={handleSubmit}>
            <Card className="p-8 mb-6">
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-6">Basic Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Scheme Name *</label>
                  <Input
                    type="text"
                    value={formData.scheme_name}
                    onChange={(e) => setFormData({ ...formData, scheme_name: e.target.value })}
                    required
                    placeholder="e.g., PM SVANidhi"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Ministry/Department *</label>
                    <Input
                      type="text"
                      value={formData.ministry_department}
                      onChange={(e) => setFormData({ ...formData, ministry_department: e.target.value })}
                      required
                      placeholder="e.g., Ministry of Finance"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Government Level *</label>
                    <select
                      value={formData.government_level}
                      onChange={(e) => setFormData({ ...formData, government_level: e.target.value })}
                      className="w-full border rounded-md p-2"
                      required
                    >
                      <option value="Central">Central</option>
                      <option value="State">State</option>
                      <option value="Local">Local</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Launch Year</label>
                    <Input
                      type="number"
                      value={formData.launch_year}
                      onChange={(e) => setFormData({ ...formData, launch_year: parseInt(e.target.value) })}
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full border rounded-md p-2"
                      required
                    >
                      <option value="active">Active</option>
                      <option value="pilot">Pilot</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 mb-6">
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-6">Classification (CSED Taxonomy)</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Primary Pillar *</label>
                  <select
                    value={formData.primary_pillar}
                    onChange={(e) => handlePillarChange(e.target.value)}
                    className="w-full border rounded-md p-2"
                    required
                  >
                    <option value="">Select Pillar</option>
                    {pillars.map((pillar) => (
                      <option key={pillar.id} value={pillar.pillar_name}>
                        {pillar.icon} {pillar.pillar_name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.primary_pillar && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Themes (select multiple)</label>
                    <div className="border rounded-md p-4 max-h-48 overflow-y-auto">
                      {selectedThemes.map((theme) => (
                        <label key={theme.id} className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            checked={formData.themes.includes(theme.theme_name)}
                            onChange={(e) => {
                              const newThemes = e.target.checked
                                ? [...formData.themes, theme.theme_name]
                                : formData.themes.filter(t => t !== theme.theme_name);
                              setFormData({ ...formData, themes: newThemes });
                            }}
                          />
                          <span className="text-sm">{theme.theme_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Keywords (comma-separated)
                  </label>
                  <Input
                    type="text"
                    value={formData.keywords.join(', ')}
                    onChange={(e) => handleArrayInput('keywords', e.target.value)}
                    placeholder="e.g., microfinance, credit, entrepreneurship"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-8 mb-6">
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-6">Target & Applicability</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Target Beneficiaries (comma-separated)
                  </label>
                  <Input
                    type="text"
                    value={formData.target_beneficiaries.join(', ')}
                    onChange={(e) => handleArrayInput('target_beneficiaries', e.target.value)}
                    placeholder="e.g., MSMEs, Street Vendors, Farmers"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Sector/Industry (comma-separated)
                  </label>
                  <Input
                    type="text"
                    value={formData.sector_industry.join(', ')}
                    onChange={(e) => handleArrayInput('sector_industry', e.target.value)}
                    placeholder="e.g., Manufacturing, Agriculture, Services"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Use Case Applicability</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.applies_to_msmes}
                        onChange={(e) => setFormData({ ...formData, applies_to_msmes: e.target.checked })}
                      />
                      <span>Applies to MSMEs</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.applies_to_ngos}
                        onChange={(e) => setFormData({ ...formData, applies_to_ngos: e.target.checked })}
                      />
                      <span>Applies to NGOs</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.applies_to_csr_programs}
                        onChange={(e) => setFormData({ ...formData, applies_to_csr_programs: e.target.checked })}
                      />
                      <span>Applies to CSR Programs</span>
                    </label>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 mb-6">
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-6">Detailed Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Objective Summary *</label>
                  <textarea
                    value={formData.objective_summary}
                    onChange={(e) => setFormData({ ...formData, objective_summary: e.target.value })}
                    className="w-full border rounded-md p-2 min-h-[100px]"
                    required
                    placeholder="Brief overview of the scheme objectives..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Key Components (one per line)
                  </label>
                  <textarea
                    value={formData.key_components.join('\n')}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      key_components: e.target.value.split('\n').filter(item => item.trim())
                    })}
                    className="w-full border rounded-md p-2 min-h-[120px]"
                    placeholder="- Component 1&#10;- Component 2&#10;- Component 3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Eligibility Criteria *</label>
                  <textarea
                    value={formData.eligibility_criteria}
                    onChange={(e) => setFormData({ ...formData, eligibility_criteria: e.target.value })}
                    className="w-full border rounded-md p-2 min-h-[100px]"
                    required
                    placeholder="Who can apply for this scheme..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Financial Benefits/Allocation *</label>
                  <textarea
                    value={formData.financial_benefits_allocation}
                    onChange={(e) => setFormData({ ...formData, financial_benefits_allocation: e.target.value })}
                    className="w-full border rounded-md p-2 min-h-[100px]"
                    required
                    placeholder="Financial benefits provided..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Implementation Mechanism *</label>
                  <textarea
                    value={formData.implementation_mechanism}
                    onChange={(e) => setFormData({ ...formData, implementation_mechanism: e.target.value })}
                    className="w-full border rounded-md p-2 min-h-[100px]"
                    required
                    placeholder="How the scheme is implemented..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Application Process *</label>
                  <textarea
                    value={formData.application_process}
                    onChange={(e) => setFormData({ ...formData, application_process: e.target.value })}
                    className="w-full border rounded-md p-2 min-h-[100px]"
                    required
                    placeholder="Step-by-step application process..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Timeline & Deadlines</label>
                  <textarea
                    value={formData.timeline_deadlines}
                    onChange={(e) => setFormData({ ...formData, timeline_deadlines: e.target.value })}
                    className="w-full border rounded-md p-2 min-h-[80px]"
                    placeholder="Application deadlines and timelines..."
                  />
                </div>
              </div>
            </Card>

            <Card className="p-8 mb-6">
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-6">Official Resources</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Official Website</label>
                  <Input
                    type="url"
                    value={formData.official_website}
                    onChange={(e) => setFormData({ ...formData, official_website: e.target.value })}
                    placeholder="https://example.gov.in"
                  />
                </div>
              </div>
            </Card>

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#E87722] hover:bg-[#d66a1a]"
              >
                {saving ? 'Saving...' : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isEdit ? 'Update Scheme' : 'Create Scheme'}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/schemes')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}
